import React, { useMemo, useRef } from 'react';
import { useWaitlistEventStatus, useWaitlistEventStatuses } from '../hooks/useApi';
import { WaitlistEventStatusAccount, WaitlistEventStatusResponse } from '../types';
import {
  WaitlistBadge,
  WaitlistBadgeOptions,
  formatSyncedAgo,
  holdsTickets,
  waitlistAccountsById,
  waitlistBadgesFor,
  waitlistPickerRank,
} from './waitlistStatus';

export interface WaitlistLookup {
  /** This account's row on the event: tickets held plus entry, if any. */
  statusFor: (accountId: number) => WaitlistEventStatusAccount | undefined;
  /** Flags for a row, waitlist state first. Empty when the row is clean.
   *  See WaitlistBadgeOptions: the tier being joined stops the badges
   *  repeating it, and ticket ownership is opt-in. */
  badgesFor: (accountId: number, options?: WaitlistBadgeOptions) => WaitlistBadge[];
  /** Tickets already held for this event — the reason a row starts unchecked. */
  holdsTickets: (accountId: number) => boolean;
  /** Picker order: free to join, then holds tickets, then on the waitlist.
   *  Stable within each group, so the caller's own order survives — and frozen
   *  once computed, so a row never moves under the customer mid-interaction. */
  sortAccounts: <T extends { id: number }>(accounts: T[]) => T[];
  /** How many accounts already hold tickets — for a summary line. */
  withTicketsCount: number;
  /** How many accounts are already on this waitlist — for a summary line. */
  onWaitlistCount: number;
  /** Freshness of the underlying sync — a join made in the Dice app since the
   *  last sync won't appear here, so the picker should say how old this is. */
  syncedLabel: string;
  isLoading: boolean;
}

/**
 * Row order, decided once per event and then held.
 *
 * Sorting live would move a row the instant its badge changed — join an
 * account and it jumps into the waitlist group, taking the confirmation the
 * customer was looking at with it. The order is a way to read the list, not a
 * live readout, so it is fixed the first time real data arrives and left alone
 * until the picker is mounted afresh.
 */
export type FrozenOrder = Map<string, number[]>;

const orderedByFreeze = <T extends { id: number }>(accounts: T[], order: number[]): T[] => {
  const rank = new Map(order.map((id, i) => [id, i]));
  // An account added since the freeze has no place in it; park it at the end
  // rather than dropping it or re-sorting everything around it.
  return [...accounts].sort(
    (a, b) => (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
};

/** Turns one event-status response into the lookup every picker row reads. */
export const buildWaitlistLookup = (
  data: WaitlistEventStatusResponse | undefined,
  isLoading: boolean,
  frozenOrder?: FrozenOrder,
): WaitlistLookup => {
  const byAccount = waitlistAccountsById(data?.accounts ?? []);

  let withTicketsCount = 0;
  let onWaitlistCount = 0;
  byAccount.forEach((account) => {
    if (account.active_tickets > 0) withTicketsCount += 1;
    // Ownership is counted separately above, and a timed-out entry is not a
    // live spot on the list — neither belongs in "already on this waitlist".
    else if (account.waitlist && account.waitlist.status !== 'timed-out') onWaitlistCount += 1;
  });

  return {
    statusFor: (accountId) => byAccount.get(accountId),
    badgesFor: (accountId, options) => waitlistBadgesFor(byAccount.get(accountId), options),
    holdsTickets: (accountId) => holdsTickets(byAccount.get(accountId)),
    sortAccounts: (accounts) => {
      const eventId = data?.event_id;
      const frozen = eventId ? frozenOrder?.get(eventId) : undefined;
      if (frozen) return orderedByFreeze(accounts, frozen);

      // Copy first: the caller's array is a memoised list it reuses elsewhere.
      const sorted = [...accounts].sort(
        (a, b) => waitlistPickerRank(byAccount.get(a.id)) - waitlistPickerRank(byAccount.get(b.id)),
      );
      // Freeze only once the ranks mean something. Before the response lands
      // every row ranks 0, and freezing that would pin the list in whatever
      // order the accounts happened to arrive.
      if (eventId && frozenOrder && !isLoading && byAccount.size > 0) {
        frozenOrder.set(eventId, sorted.map((a) => a.id));
      }
      return sorted;
    },
    withTicketsCount,
    onWaitlistCount,
    syncedLabel: formatSyncedAgo(data?.sync?.last_complete_at ?? null),
    isLoading,
  };
};

/**
 * Lookups for several events at once, keyed by event id.
 *
 * Callers that decide the default selection need this above the picker: an
 * account already holding tickets is dropped from the default set, and the
 * join payload has to apply the same rule the checkboxes show.
 */
export const useWaitlistLookups = (eventIds: string[]): Map<string, WaitlistLookup> => {
  const results = useWaitlistEventStatuses(eventIds);
  // Survives every re-render of the screen, so the order outlives a join.
  const frozenOrder = useRef<FrozenOrder>(new Map()).current;

  // Rebuilt every render rather than memoised: it is a map over a handful of
  // accounts for the handful of events currently expanded, and a stale lookup
  // here would mean checkboxes disagreeing with the payload.
  const map = new Map<string, WaitlistLookup>();
  eventIds.forEach((eventId, i) => {
    const result = results[i];
    map.set(eventId, buildWaitlistLookup(result?.data, result?.isLoading ?? true, frozenOrder));
  });
  return map;
};

/** An empty lookup, for an event whose status hasn't been requested. */
export const emptyWaitlistLookup = (): WaitlistLookup => buildWaitlistLookup(undefined, true);

interface Props {
  /** Dice event id. Null disables the fetch. */
  eventId: string | null;
  children: (lookup: WaitlistLookup) => React.ReactNode;
}

/**
 * Fetches one event's account status and hands the parent a lookup, so each UI
 * tree keeps its own row markup without firing a query per account.
 *
 * Purely advisory: nothing here disables an account, and a row this marks as
 * already holding tickets stays checkable. Failures are swallowed on purpose —
 * a status outage shouldn't block joining.
 */
export const WaitlistStatusLookup: React.FC<Props> = ({ eventId, children }) => {
  const { data, isLoading } = useWaitlistEventStatus(eventId);
  const frozenOrder = useRef<FrozenOrder>(new Map()).current;
  const lookup = useMemo(
    () => buildWaitlistLookup(data, isLoading, frozenOrder),
    [data, isLoading, frozenOrder],
  );

  return <>{children(lookup)}</>;
};
